import { useState, useCallback } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent';
import { PageHeader } from '../components/templates/PageHeader';
import { Button } from '../components/atoms/Button';
import { Alert } from '../components/atoms/Alert/Alert';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { ConfirmDialog } from '../components/ui/ConfirmDialog/ConfirmDialog';
import { useTranslation } from '../i18n/useTranslation';
import {
  ComparisonView,
  ComparisonPhotoPicker,
  type ComparisonPhotoPickerState,
  type ComparisonPickedPhoto,
  type ComparisonPreset,
  type ComparisonViewMode,
  type ComparisonErrorKind,
  type ComparisonResult,
  type ComparisonViewState,
} from '../components/organisms/ComparisonView';
import {
  useComparisonHistory,
  useComparisonDetail,
  useCreateComparison,
  useComparisonGallery,
  useDeleteComparison,
  useDeleteAllComparisons,
} from '../hooks/useAquariumComparisons';
import {
  buildImageIdsPayload,
  MAX_COMPARISON_IMAGES,
  MIN_COMPARISON_IMAGES,
  type CreateComparisonPayload,
} from '../services/aquariumComparisonService';
import { getAiLimitErrorKind } from '../services/aiUsageService';
import { useAiUsage } from '../hooks/useAiUsage';
import { AiUsageMeter } from '../components/aiUsage/AiUsageMeter';

const PRESET_DAYS: Record<Exclude<ComparisonPreset, 'custom' | 'photos'>, 7 | 30 | 90> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
};

function readErrorField(error: unknown, field: 'code' | 'message' | 'status'): unknown {
  return typeof error === 'object' && error !== null
    ? (error as Record<string, unknown>)[field]
    : undefined;
}

/**
 * Maps a create-comparison failure onto ComparisonView's error copy — never the
 * raw backend text. The backend answers selection problems with a 400
 * VALIDATION_ERROR and a descriptive message (src/services/aquariumComparisonService.ts,
 * src/controllers/aquariumComparison.controller.ts); dedicated codes are honoured
 * too. AI_BUDGET_EXCEEDED (429) and AI_TEMPORARILY_UNAVAILABLE / any 503 map to
 * the AI copy. With explicitly chosen photos (`photos`), selection errors get the
 * photo-specific copy.
 */
function mapErrorToKind(error: unknown, photos = false): ComparisonErrorKind {
  const aiLimit = getAiLimitErrorKind(error);
  if (aiLimit === 'exceeded') return 'aiBudgetExceeded';
  if (aiLimit === 'unavailable') return 'aiUnavailable';
  const code = readErrorField(error, 'code');
  const status = readErrorField(error, 'status');
  if (status === 503) return 'aiUnavailable';
  const message = readErrorField(error, 'message');
  const text = typeof message === 'string' ? message : '';
  if (photos) {
    if (status === 404 || /do not belong to this aquarium|duplicate|valid UUIDs/i.test(text)) {
      return 'photosInvalid';
    }
    if (code === 'INSUFFICIENT_IMAGES' || /at least \d+ (images|items)/i.test(text)) {
      return 'photosTooFew';
    }
    if (code === 'TOO_MANY_IMAGES' || /no more than \d+ images|not exceed \d+ items/i.test(text)) {
      return 'photosTooMany';
    }
    return 'generic';
  }
  if (code === 'INSUFFICIENT_IMAGES' || /could not find two distinct images|at least \d+ images/i.test(text)) {
    return 'insufficientImages';
  }
  if (code === 'TOO_MANY_IMAGES' || /no more than \d+ images/i.test(text)) {
    return 'tooManyImages';
  }
  return 'generic';
}

function isAccessDenied(error: unknown): boolean {
  const status = readErrorField(error, 'status');
  return status === 403 || status === 404;
}

/** The delete waiting for confirmation: one past comparison or all of them. */
type PendingDelete = { readonly kind: 'one'; readonly id: string } | { readonly kind: 'all' } | null;

function hasNearestMatch(result: ComparisonResult): boolean {
  return Boolean(result.beforeImage.isNearestMatch || result.afterImage.isNearestMatch);
}

export function AquariumComparisonPage() {
  const { aquariumId } = useParams<{ aquariumId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedPreset, setSelectedPreset] = useState<ComparisonPreset>('7d');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [viewMode, setViewMode] = useState<ComparisonViewMode>('sideBySide');
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<ComparisonErrorKind | null>(null);
  const [selectedPhotos, setSelectedPhotos] = useState<ComparisonPickedPhoto[]>([]);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete>(null);
  const [deleteFailed, setDeleteFailed] = useState<'one' | 'all' | null>(null);
  const [deleteAnnouncement, setDeleteAnnouncement] = useState('');

  const historyQuery = useComparisonHistory(aquariumId);
  const createMutation = useCreateComparison(aquariumId);
  const detailQuery = useComparisonDetail(aquariumId, selectedHistoryId);
  const deleteMutation = useDeleteComparison(aquariumId);
  const deleteAllMutation = useDeleteAllComparisons(aquariumId);
  const isDeleting = deleteMutation.isPending || deleteAllMutation.isPending;
  const { exceeded: aiCreditsUsedUp } = useAiUsage();
  const isPhotoMode = selectedPreset === 'photos';
  const galleryQuery = useComparisonGallery(aquariumId, isPhotoMode);

  const history = historyQuery.data ?? [];
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = historyQuery;
  const isCreating = createMutation.isPending;
  const isDetailLoading = selectedHistoryId !== null && detailQuery.isLoading;
  const detailFailed = selectedHistoryId !== null && Boolean(detailQuery.error);

  const handleSelectPreset = useCallback((preset: ComparisonPreset) => {
    setSelectedPreset(preset);
    setErrorKind(null);
  }, []);

  const handleChangeCustomDateFrom = useCallback((value: string) => {
    setCustomDateFrom(value);
    setErrorKind(null);
  }, []);

  const handleChangeCustomDateTo = useCallback((value: string) => {
    setCustomDateTo(value);
    setErrorKind(null);
  }, []);

  const handleTogglePhoto = useCallback((photo: ComparisonPickedPhoto) => {
    setErrorKind(null);
    setSelectedPhotos(current => {
      if (current.some(p => p.id === photo.id)) return current.filter(p => p.id !== photo.id);
      return current.length >= MAX_COMPARISON_IMAGES ? current : [...current, photo];
    });
  }, []);

  const handleOpenGallery = useCallback(() => {
    if (aquariumId) navigate(`/aquariums/${aquariumId}/gallery`);
  }, [aquariumId, navigate]);

  const { refetch: refetchGallery, fetchNextPage: fetchNextGalleryPage } = galleryQuery;
  const handleLoadMorePhotos = useCallback(() => {
    void fetchNextGalleryPage();
  }, [fetchNextGalleryPage]);
  const handleRetryGallery = useCallback(() => {
    void refetchGallery();
  }, [refetchGallery]);

  const photoSelectionValid =
    selectedPhotos.length >= MIN_COMPARISON_IMAGES && selectedPhotos.length <= MAX_COMPARISON_IMAGES;

  const handleCompare = () => {
    setErrorKind(null);
    setSelectedHistoryId(null);

    let payload: CreateComparisonPayload;
    if (isPhotoMode) {
      if (selectedPhotos.length < MIN_COMPARISON_IMAGES) {
        setErrorKind('photosTooFew');
        return;
      }
      if (selectedPhotos.length > MAX_COMPARISON_IMAGES) {
        setErrorKind('photosTooMany');
        return;
      }
      // Earliest photo first, whatever order they were picked in.
      payload = buildImageIdsPayload(selectedPhotos);
    } else if (selectedPreset === 'custom') {
      if (!customDateFrom || !customDateTo || customDateFrom > customDateTo) {
        setErrorKind('insufficientImages');
        return;
      }
      payload = { dateFrom: customDateFrom, dateTo: customDateTo };
    } else {
      payload = { dateOffsetDays: PRESET_DAYS[selectedPreset] };
    }

    const photos = 'imageIds' in payload;
    createMutation.mutate(payload, {
      onError: error => setErrorKind(mapErrorToKind(error, photos)),
    });
  };

  const handleLoadMoreHistory = useCallback(() => {
    if (isFetchingNextPage) return;
    void fetchNextPage();
  }, [fetchNextPage, isFetchingNextPage]);

  const handleSelectHistoryItem = useCallback((id: string) => {
    setErrorKind(null);
    setSelectedHistoryId(id);
  }, []);

  const handleRequestDelete = useCallback((id: string) => {
    setDeleteFailed(null);
    setPendingDelete({ kind: 'one', id });
  }, []);

  const handleRequestDeleteAll = useCallback(() => {
    setDeleteFailed(null);
    setPendingDelete({ kind: 'all' });
  }, []);

  const { reset: resetCreated, data: createdResult } = createMutation;
  const handleConfirmDelete = () => {
    const pending = pendingDelete;
    setPendingDelete(null);
    if (pending === null) return;
    setDeleteAnnouncement('');
    // A deleted comparison must not stay in the result area (reopened or just created).
    const clearShown = (deletedId: string | null) => {
      if (deletedId === null || selectedHistoryId === deletedId) setSelectedHistoryId(null);
      if (deletedId === null || createdResult?.id === deletedId) resetCreated();
    };
    if (pending.kind === 'one') {
      deleteMutation.mutate(pending.id, {
        onSuccess: () => {
          clearShown(pending.id);
          setDeleteAnnouncement(t('comparison.history.delete.doneOne'));
        },
        onError: () => setDeleteFailed('one'),
      });
    } else {
      deleteAllMutation.mutate(undefined, {
        onSuccess: (deleted) => {
          clearShown(null);
          setDeleteAnnouncement(t('comparison.history.delete.doneAll', { n: deleted }));
        },
        onError: () => setDeleteFailed('all'),
      });
    }
  };

  // A reopened history item wins over the latest run; it is fetched read-only.
  const result: ComparisonResult | null = selectedHistoryId
    ? (detailQuery.data ?? null)
    : (createMutation.data ?? null);

  const effectiveErrorKind: ComparisonErrorKind | null =
    errorKind ?? (detailFailed ? 'generic' : null);

  let state: ComparisonViewState;
  if (isCreating || isDetailLoading) {
    state = 'loading';
  } else if (effectiveErrorKind !== null) {
    state = 'error';
  } else if (result) {
    state = hasNearestMatch(result) ? 'partial' : 'populated';
  } else {
    state = 'empty';
  }

  const galleryPhotos = galleryQuery.data ?? [];
  let pickerState: ComparisonPhotoPickerState;
  if (galleryQuery.isPending) {
    pickerState = 'loading';
  } else if (galleryQuery.isError && galleryPhotos.length === 0) {
    pickerState = 'error';
  } else if (galleryPhotos.length === 0) {
    pickerState = 'empty';
  } else {
    pickerState = 'ready';
  }

  // Not the owner (or no such aquarium): leave the page, like other aquarium-scoped pages.
  if (isAccessDenied(historyQuery.error)) {
    return <Navigate to="/aquariums" replace />;
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="wide">
          <PageHeader
            title={t('comparison.page.title')}
            description={t('comparison.page.subtitle')}
            actions={
              <Button
                variant="primary"
                disabled={state === 'loading' || aiCreditsUsedUp || (isPhotoMode && !photoSelectionValid)}
                onClick={handleCompare}
              >
                {t('comparison.action.compare')}
              </Button>
            }
          />
          <AiUsageMeter variant="compact" framed />
          <ComparisonView
            state={state}
            selectedPreset={selectedPreset}
            photoPicker={
              <ComparisonPhotoPicker
                state={pickerState}
                photos={galleryPhotos}
                selected={selectedPhotos}
                min={MIN_COMPARISON_IMAGES}
                max={MAX_COMPARISON_IMAGES}
                disabled={state === 'loading'}
                onToggle={handleTogglePhoto}
                hasMore={Boolean(galleryQuery.hasNextPage)}
                isLoadingMore={galleryQuery.isFetchingNextPage}
                onLoadMore={handleLoadMorePhotos}
                onRetry={handleRetryGallery}
                onOpenGallery={handleOpenGallery}
              />
            }
            customDateFrom={customDateFrom}
            customDateTo={customDateTo}
            viewMode={viewMode}
            result={result}
            errorKind={effectiveErrorKind}
            history={history}
            hasMoreHistory={Boolean(hasNextPage)}
            isLoadingMoreHistory={isFetchingNextPage}
            onLoadMoreHistory={handleLoadMoreHistory}
            selectedHistoryId={selectedHistoryId}
            onSelectPreset={handleSelectPreset}
            onChangeCustomDateFrom={handleChangeCustomDateFrom}
            onChangeCustomDateTo={handleChangeCustomDateTo}
            onCompare={handleCompare}
            onRetry={handleCompare}
            onChangeViewMode={setViewMode}
            onSelectHistoryItem={handleSelectHistoryItem}
            onDeleteHistoryItem={handleRequestDelete}
            onDeleteAllHistory={handleRequestDeleteAll}
            isDeletingHistory={isDeleting}
            historyStatus={
              deleteFailed ? (
                <Alert onDismiss={() => setDeleteFailed(null)}>
                  {t(deleteFailed === 'one' ? 'comparison.history.delete.errorOne' : 'comparison.history.delete.errorAll')}
                </Alert>
              ) : undefined
            }
          />
          <LiveRegion visuallyHidden>{deleteAnnouncement}</LiveRegion>
          <ConfirmDialog
            isOpen={pendingDelete?.kind === 'one'}
            title={t('comparison.history.delete.confirmOneTitle')}
            message={t('comparison.history.delete.confirmOneMessage')}
            confirmLabel={t('comparison.history.delete.confirmOne')}
            cancelLabel={t('comparison.history.delete.cancel')}
            onConfirm={handleConfirmDelete}
            onCancel={() => setPendingDelete(null)}
          />
          <ConfirmDialog
            isOpen={pendingDelete?.kind === 'all'}
            title={t('comparison.history.delete.confirmAllTitle')}
            message={t('comparison.history.delete.confirmAllMessage')}
            confirmLabel={t('comparison.history.delete.confirmAll')}
            cancelLabel={t('comparison.history.delete.cancel')}
            onConfirm={handleConfirmDelete}
            onCancel={() => setPendingDelete(null)}
          />
        </PageContent>
      }
    />
  );
}

export default AquariumComparisonPage;
